import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { PERCENT_THRESHOLD } from '@osmosis/constants';

import { useFetch } from '../context/FetchProvider';
import { useUserContext } from '../context/UserContext';
import { useVideoPlayer } from '../context/VideoPlayerProvider';

const POLL_INTERVAL_MS = 15_000;
// We'll stop polling after a while, cron might have failed?
const POLL_TIMEOUT_MS = 5 * 60_000;

type Props = {
  mediaID?: number;
};

type Result = {
  confirmed: boolean;
  // True once confirmed and playback has stopped. */
  shouldAnnounce: boolean;
};

/**
 * Resolves whether the current video is confirmed watched in the database.
 */
export default function useWatchedConfirmation({ mediaID }: Props): Result {
  const { get } = useFetch();
  const { user } = useUserContext();
  const { getPlayer, progressState, playing } = useVideoPlayer();

  const [crossedAt, setCrossedAt] = useState<number | null>(null);
  const [shouldAnnounce, setShouldAnnounce] = useState(false);

  const duration = getPlayer()?.getDuration();
  const percent = duration ? (progressState.playedSeconds / duration) * 100 : 0;
  const hasCrossed = percent >= PERCENT_THRESHOLD;

  // Rewinding below the threshold shouldn't a poll that is already pending.
  useEffect(() => {
    if (hasCrossed) setCrossedAt((prev) => prev ?? Date.now());
  }, [hasCrossed]);

  const { data } = useQuery(
    ['watchedConfirmed', mediaID],
    async () => {
      const res = await get(`/api/learn/watched?mediaID=${mediaID}`);
      return { confirmed: !!res?.data?.confirmed };
    },
    {
      enabled: !!user && !!mediaID,
      refetchInterval: (current) => {
        if (current?.confirmed || !crossedAt) return false;
        if (Date.now() - crossedAt > POLL_TIMEOUT_MS) return false;
        return POLL_INTERVAL_MS;
      },
    },
  );

  const confirmed = !!data?.confirmed;

  // For accessibility, announce when the video ends or is paused and confirmed watched.
  useEffect(() => {
    if (confirmed && !playing) setShouldAnnounce(true);
  }, [confirmed, playing]);

  return { confirmed, shouldAnnounce };
}
